<script lang="ts">
	// One chat row of the sidebar (Chats list and folders alike): activity dot,
	// unread dot, pin marker, title (double-click or menu → inline rename),
	// compact time-ago, and the "…" menu (Rename · Pin · Open in split view ·
	// Move to folder · Move to Trash). In selection mode a checkbox leads the
	// row. Rows are draggable onto folders (SAY-1820).
	import { tick } from 'svelte';
	import { page } from '$app/state';
	import { goto } from '$app/navigation';
	import PencilIcon from '@lucide/svelte/icons/pencil';
	import Trash2Icon from '@lucide/svelte/icons/trash-2';
	import EllipsisIcon from '@lucide/svelte/icons/ellipsis';
	import FolderInputIcon from '@lucide/svelte/icons/folder-input';
	import FolderXIcon from '@lucide/svelte/icons/folder-x';
	import Columns2Icon from '@lucide/svelte/icons/columns-2';
	import PinIcon from '@lucide/svelte/icons/pin';
	import PinOffIcon from '@lucide/svelte/icons/pin-off';
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js';
	import Spinner from './Spinner.svelte';
	import SelectCheckbox from './SelectCheckbox.svelte';
	import {
		getRelativeTimeTicker,
		markRenamed,
		markTrashed,
		markRestored,
		upsertSession,
		type SessionRow,
	} from '$lib/sessions-store.svelte';
	import { renameSession, trashSession, restoreSession } from '$lib/api';
	import { isScheduledUnread } from '$lib/scheduled-store.svelte';
	import { getFolders, folderOf, isPinned, moveToFolder, setPinned } from '$lib/folders-store.svelte';
	import { selectedIds } from '$lib/chat-selection.svelte';
	import { forgetTab, setSplit } from '$lib/chat-tabs.svelte';
	import { dragPayload, setDraggedChats } from '$lib/chat-dnd';
	import { formatAgo, formatFull } from '$lib/session-time';
	import { toast } from 'svelte-sonner';

	let {
		row,
		isActive = false,
		selectable = false,
		selected = false,
		onToggleSelect,
	}: {
		row: SessionRow;
		isActive?: boolean;
		selectable?: boolean;
		selected?: boolean;
		onToggleSelect?: (on: boolean) => void;
	} = $props();

	const folders = getFolders();
	const currentFolder = $derived(folderOf(row.sessionId));
	const pinned = $derived(isPinned(row.sessionId));
	// Reading the ticker re-renders the compact time-ago once a minute.
	const ago = $derived.by(() => {
		void getRelativeTimeTicker();
		return formatAgo(row.lastActivityAt);
	});

	let editing = $state(false);
	let draftName = $state('');
	let busy = $state(false);
	let dragging = $state(false);
	let editInput: HTMLInputElement | undefined = $state();

	function startRename(event?: Event): void {
		event?.preventDefault();
		event?.stopPropagation();
		editing = true;
		draftName = row.name ?? '';
		void tick().then(() => {
			editInput?.focus();
			editInput?.select();
		});
	}

	function cancelRename(): void {
		editing = false;
		draftName = '';
	}

	async function commitRename(): Promise<void> {
		// Re-entrancy guard: Enter sets busy → disables the input → blur fires a
		// second commit while the first PATCH is in flight.
		if (!editing || busy) return;
		const next = draftName.trim();
		if (!next || next === row.name) {
			cancelRename();
			return;
		}
		busy = true;
		try {
			const res = await renameSession(row.sessionId, next);
			upsertSession({ sessionId: row.sessionId, name: res.name });
			markRenamed(row.sessionId);
		} catch (err) {
			toast.error('Rename failed', {
				description: err instanceof Error ? err.message : 'Server did not accept the new name.',
			});
		} finally {
			busy = false;
			cancelRename();
		}
	}

	function onRenameKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			void commitRename();
		} else if (event.key === 'Escape') {
			event.preventDefault();
			cancelRename();
		}
	}

	// Move to Trash (SAY-1463): reversible, no modal, Undo in the toast.
	async function trashOne(): Promise<void> {
		markTrashed(row.sessionId);
		forgetTab(row.sessionId);
		if (isActive) void goto('/');
		try {
			await trashSession(row.sessionId);
			toast.success('Moved to Trash', {
				description: row.name ?? row.sessionId.slice(0, 8),
				action: {
					label: 'Undo',
					onClick: () => {
						markRestored(row.sessionId);
						void restoreSession(row.sessionId).catch((err) => {
							markTrashed(row.sessionId);
							toast.error('Undo failed', { description: err instanceof Error ? err.message : String(err) });
						});
					},
				},
			});
		} catch (err) {
			markRestored(row.sessionId);
			toast.error('Could not move to Trash', { description: err instanceof Error ? err.message : String(err) });
		}
	}

	async function move(folderId: string | null): Promise<void> {
		try {
			await moveToFolder([row.sessionId], folderId);
		} catch (err) {
			toast.error('Could not move the chat', { description: err instanceof Error ? err.message : String(err) });
		}
	}

	async function togglePin(): Promise<void> {
		try {
			await setPinned(row.sessionId, !pinned);
		} catch (err) {
			toast.error(pinned ? 'Could not unpin the chat' : 'Could not pin the chat', {
				description: err instanceof Error ? err.message : String(err),
			});
		}
	}

	// Split view (SAY-1820): show this chat beside the one that is open. From a
	// non-chat page there is nothing to sit beside, so just open it.
	function openInSplit(): void {
		const current = page.params.sessionId;
		if (current && current !== row.sessionId) setSplit(row.sessionId);
		else void goto('/s/' + row.sessionId);
	}

	// Drag the row (or the whole selection it belongs to) onto a folder.
	function onDragStart(e: DragEvent): void {
		setDraggedChats(e.dataTransfer, dragPayload(row.sessionId, selectedIds()));
		dragging = true;
	}
</script>

<div
	id="sidebar-chat-group"
	class="group/reveal relative w-full {dragging ? 'opacity-50' : ''}"
	role="listitem"
	data-testid="chat-item"
	data-session-id={row.sessionId}
	draggable={!editing}
	ondragstart={onDragStart}
	ondragend={() => (dragging = false)}
>
	<div
		id="sidebar-chat-item"
		class="flex w-full justify-between rounded-xl px-2 py-1.5 whitespace-nowrap text-ellipsis transition {isActive || editing || selected
			? 'bg-black/[0.035] dark:bg-white/[0.045]'
			: 'hover:bg-gray-100 dark:hover:bg-gray-900'}"
	>
		{#if selectable}
			<SelectCheckbox
				class="mr-2 self-center"
				checked={selected}
				label="Select chat {row.name ?? row.sessionId.slice(0, 8)}"
				onchange={(on) => onToggleSelect?.(on)}
			/>
		{/if}
		{#if editing}
			<input
				id="chat-title-input-{row.sessionId}"
				bind:this={editInput}
				bind:value={draftName}
				onkeydown={onRenameKeydown}
				onblur={() => void commitRename()}
				class="mr-10 h-5 w-full bg-transparent text-[0.8125rem] outline-hidden"
				aria-label="Rename session"
				placeholder="Chat name"
				disabled={busy}
			/>
		{:else if selectable}
			<button
				type="button"
				class="-my-1.5 flex min-w-0 flex-1 items-center py-1.5 text-left"
				onclick={() => onToggleSelect?.(!selected)}
			>
				<span dir="auto" class="h-5 w-full self-center truncate">{row.name ?? row.sessionId.slice(0, 8)}</span>
			</button>
		{:else}
			<a
				href={'/s/' + row.sessionId}
				aria-current={isActive ? 'page' : undefined}
				title={formatFull(row.lastActivityAt)}
				ondblclick={(e) => startRename(e)}
				draggable="false"
				class="-my-1.5 flex min-w-0 flex-1 items-center py-1.5"
			>
				{#if row.activity === 'running'}
					<span class="shrink-0 self-center pr-2" data-testid="activity-dot" data-state="running" aria-label="running">
						<Spinner class="size-3" />
					</span>
				{:else if row.activity === 'errored'}
					<span
						class="mr-2 inline-block size-1.5 shrink-0 self-center rounded-full bg-red-500"
						data-testid="activity-dot"
						data-state="errored"
						aria-label="errored"
					></span>
				{/if}
				{#if pinned}
					<PinIcon class="mr-1.5 size-3 shrink-0 self-center text-gray-400 dark:text-gray-500" strokeWidth={2} aria-label="Pinned" data-testid="pin-marker" />
				{/if}
				<span class="flex w-full min-w-0 flex-1 self-center">
					{#if isScheduledUnread(row.sessionId)}
						<span class="flex shrink-0 self-center pr-2.5 transition-opacity duration-300">
							<span
								class="size-1.5 rounded-full bg-sky-500"
								data-testid="scheduled-unread-dot"
								aria-label="new scheduled run"
								title="New scheduled run — not yet opened"
							></span>
						</span>
					{/if}
					<span
						dir="auto"
						class="h-5 w-full self-center truncate text-left {isScheduledUnread(row.sessionId)
							? 'text-gray-800 dark:text-gray-200'
							: ''}">{row.name ?? row.sessionId.slice(0, 8)}</span
					>
				</span>
			</a>
			<span
				class="shrink-0 self-center pl-2 text-[0.625rem] text-gray-400 group-hover/reveal:invisible group-focus-within/reveal:invisible dark:text-gray-500"
				aria-hidden="true">{ago}</span
			>
		{/if}
	</div>
	{#if !editing && !selectable}
		<div id="sidebar-chat-item-menu" class="hover-reveal absolute inset-y-0 right-1 mr-1.5 flex items-center">
			<DropdownMenu.Root>
				<DropdownMenu.Trigger
					class="m-0 flex size-5 items-center justify-center self-center transition dark:hover:text-white"
					aria-label="Chat menu"
					title="More"
					data-testid="chat-item-menu"
				>
					<EllipsisIcon class="size-3.5" strokeWidth={2} aria-hidden="true" />
				</DropdownMenu.Trigger>
				<DropdownMenu.Content align="end" sideOffset={6} class="min-w-[12.5rem]">
					<DropdownMenu.Item onSelect={() => startRename()}>
						<PencilIcon strokeWidth={1.5} aria-hidden="true" />
						Rename
					</DropdownMenu.Item>
					<DropdownMenu.Item onSelect={() => void togglePin()}>
						{#if pinned}
							<PinOffIcon strokeWidth={1.5} aria-hidden="true" />
							Unpin
						{:else}
							<PinIcon strokeWidth={1.5} aria-hidden="true" />
							Pin to top
						{/if}
					</DropdownMenu.Item>
					<DropdownMenu.Item onSelect={openInSplit}>
						<Columns2Icon strokeWidth={1.5} aria-hidden="true" />
						Open in split view
					</DropdownMenu.Item>
					{#if folders.length > 0}
						<DropdownMenu.Separator />
						{#each folders as f (f.id)}
							{#if f.id !== currentFolder}
								<DropdownMenu.Item onSelect={() => void move(f.id)}>
									<FolderInputIcon strokeWidth={1.5} aria-hidden="true" />
									<span class="truncate">Move to {f.name}</span>
								</DropdownMenu.Item>
							{/if}
						{/each}
						{#if currentFolder}
							<DropdownMenu.Item onSelect={() => void move(null)}>
								<FolderXIcon strokeWidth={1.5} aria-hidden="true" />
								Remove from folder
							</DropdownMenu.Item>
						{/if}
					{/if}
					<DropdownMenu.Separator />
					<DropdownMenu.Item onSelect={() => void trashOne()}>
						<Trash2Icon strokeWidth={1.7} aria-hidden="true" />
						Move to Trash
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Root>
		</div>
	{/if}
</div>
